import { isAxiosError } from "axios";
import { ApiResponse } from "../api/auth";
import { ApiError } from "../types/error";
import {
  Address,
  AddressOptions,
  DayFormat,
  MonthFormat,
  WeekdayFormat,
  YearFormat,
} from "../types";
import { jwtDecode } from "jwt-decode";

export const WS_SERVER_URL = import.meta.env.VITE_BACKEND_BASE_URL_WS;
export const WS_ORDER_TOPIC = "/topic/orders/";
export const WS_DISH_AVAILABILITIES_TOPIC = "/topic/dish-availability";
export const MAX_VISIBILITY_DAYS = 6;
export const MIN_SPEED = 30;
export const MAX_SPEED = 50;
export const PAGE_SIZE = 5;
export const MONEY_DEVISE = "CAD";

/**
 * Gère les erreurs Axios et les normalise en ApiError.
 * @param error L'erreur capturée.
 * @param defaultMessage Message d'erreur par défaut pour les erreurs non gérées.
 */
export const handleApiError = (
  error: unknown,
  defaultMessage: string
): ApiResponse<any> => {
  if (isAxiosError(error)) {
    // Erreur avec réponse HTTP (4xx, 5xx) ou erreur réseau normalisée par l'intercepteur
    if (error.response?.data) {
      // L'erreur 401, 500, ou l'erreur réseau normalisée
      return { data: null, error: error.response.data as ApiError };
    }

    // Cas d'erreurs Axios spécifiques non gérées par l'intercepteur comme
    // les timeouts si elles n'ont pas été normalisées (moins courant avec l'intercepteur)
    return {
      data: null,
      error: {
        error: error.code || "AXIOS_ERROR",
        message: defaultMessage, // Utiliser le message fourni pour plus de contexte
        timestamp: new Date().toISOString(),
      },
    };
  }

  // Erreur non-Axios (rare en pratique pour les requêtes)
  return {
    data: null,
    error: {
      error: "UNKNOWN",
      message: `Une erreur inconnue est survenue: ${defaultMessage}`,
      timestamp: new Date().toISOString(),
    },
  };
};

export function capitalizeWords(text: string): string {
  const lowerCaseText = text.toLowerCase();

  return lowerCaseText.replace(
    /(?:^|\s|-)([a-zà-ÿ])/g,
    (match, charToCapitalize) => match.toUpperCase()
  );
}

export const formatDateByTimezone = (date: string, locale: string): string => {
  // <-- Le retour est explicitement une string
  // Récupération du timezone de l'utilisateur
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  try {
    const newDate = new Date(date);

    // Vérification de la validité de la date (gestion d'un cas où 'new Date(date)' retourne Invalid Date)
    if (isNaN(newDate.getTime())) {
      throw new Error("Date invalide.");
    }

    return newDate.toLocaleDateString(locale, {
      dateStyle: "medium",
      timeZone: tz,
    });
  } catch (e) {
    // Le typage 'e: unknown' est le plus sûr pour les blocs catch en TS
    console.error("Erreur lors du formatage de la date:", e);
    return "N/A";
  }
};

/**
 * Vérifie si un token JWT est expiré.
 * @param token Le token JWT à vérifier.
 * @returns true si le token est expiré ou invalide, false sinon.
 */
export const isTokenExpired = (token?: string): boolean => {
  if (!token) return true;

  try {
    const decoded = jwtDecode(token);
    // 'exp' est le temps d'expiration en secondes Unix (UTC)
    const expirationTime = decoded.exp;

    if (!expirationTime) {
      // Le token n'a pas de temps d'expiration défini
      return false;
    }

    // On ajoute souvent une petite marge de sécurité (par exemple, 60 secondes)
    // avant l'expiration réelle pour éviter les problèmes de latence.
    const currentTime = Date.now() / 1000; // Conversion en secondes Unix
    return expirationTime < currentTime;
  } catch (error) {
    // Échoue si le token est malformé ou invalide
    console.error("Erreur lors du décodage du token:", error);
    return true;
  }
};

export const getTodayFormatted = () => {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0"); // M+1 car mois de 0 à 11
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export interface AddressOptions {
  includeCountry?: boolean;
  multiline?: boolean;
}

// Supposons que votre type Address ressemble à ceci (ajustez si nécessaire)
export interface Address {
  street: string;
  city: string;
  province?: string; // Nouveau champ province (optionnel)
  country: string;
  zipcode: string;
  // autres champs...
}

export function formatAddress(
  addressData: Address | string,
  options: AddressOptions = {}
) {
  // ... (Options et validation restent identiques)

  const { includeCountry = true, multiline = false } = options;

  if (!addressData || typeof addressData === "string") {
    return addressData || "N/A";
  }

  const { street, city, province, provinceAbbrev, country, zipcode } =
    addressData as Address;

  if (!street || !city) {
    return "N/A";
  }

  // Déterminer le séparateur pour l'adresse (entre la rue et la ligne ville/province)
  const separator = multiline ? "\n" : ", ";

  // Séparateur spécifique pour Postes Canada : " " entre ville/province et "  " entre province/code postal
  // On utilise un seul espace pour simplifier si ce n'est pas pour une enveloppe
  const provinceZipSeparator = multiline ? "  " : " ";

  let formattedAddress = street;

  // --- NOUVELLE LOGIQUE POUR LIGNE VILLE/PROVINCE/CODE POSTAL ---
  let cityLine = city;

  if (province && provinceAbbrev) {
    // Ordre canadien : Ville [Espace] Province
    // On suppose ici que 'province' est l'abréviation (ex: QC, ON)
    cityLine += ` ${provinceAbbrev}`;
  }

  if (zipcode) {
    // Ordre canadien : ... [Deux Espaces] Code Postal (A9A 9A9)
    cityLine += `${provinceZipSeparator}${zipcode}`;
  }

  if (cityLine.length > 0) {
    formattedAddress += `${separator}${cityLine}`;
  }

  // Ajout conditionnel du pays
  if (includeCountry && country) {
    formattedAddress += `${separator}${country}`;
  }

  return formattedAddress.trim();
}

export const calculateDistance = async (
  origin: { latitude: number; longitude: number },
  destination: { latitude: number; longitude: number }
): Promise<number | null> => {
  if (
    !origin ||
    !destination ||
    !origin.latitude ||
    !origin.longitude ||
    !destination.latitude ||
    !destination.longitude
  ) {
    return null;
  }

  return new Promise((resolve, reject) => {
    if (
      window.google &&
      window.google.maps &&
      window.google.maps.DistanceMatrixService
    ) {
      const service = new window.google.maps.DistanceMatrixService();
      service.getDistanceMatrix(
        {
          origins: [
            new window.google.maps.LatLng(origin.latitude, origin.longitude),
          ],
          destinations: [
            new window.google.maps.LatLng(
              destination.latitude,
              destination.longitude
            ),
          ],
          travelMode: window.google.maps.TravelMode.DRIVING,
        },
        (response: any, status: any) => {
          if (status === "OK" && response.rows[0].elements[0].status === "OK") {
            const distanceInMeters =
              response.rows[0].elements[0].distance.value;
            resolve(distanceInMeters);
          } else {
            reject(status);
          }
        }
      );
    } else {
      resolve(null);
    }
  });
};
